import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

// ============================================================================
// brioCAST — DeviceOps Dashboard (D4.3B-1)
// ============================================================================
// Nav item:  { id: "deviceops", label: "設備運維", icon: "🖥️", cap: "SCREEN_NETWORK", group: "deviceops" }
// Route:     case "deviceops": return hasCap(account, "SCREEN_NETWORK") ? <DeviceOpsPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
// API:       /api/partner/deviceops/*
// ============================================================================

const API = "https://api.briolabs.io";

async function api(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || json.code || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = json.code;
    throw err;
  }
  return json;
}

// Dark theme (matches FleetDashboard.jsx)
const t = {
  bg: "#0F1117", card: "#1A1D27", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", accentBg: "rgba(108,92,231,0.12)",
  success: "#00B894", successBg: "rgba(0,184,148,0.12)",
  warning: "#FDCB6E", warningBg: "rgba(253,203,110,0.15)",
  danger: "#E17055", dangerBg: "rgba(225,112,85,0.12)",
  text: "#E8E8ED", textDim: "#8B8FA3", muted: "#8B8FA3", dim: "#5A5E72",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

const STATUS = {
  healthy:   { bg: "#10B98122", fg: "#10B981", label: "正常",   sev: 0 },
  warning:   { bg: "#F59E0B22", fg: "#F59E0B", label: "警告",   sev: 1 },
  degraded:  { bg: "#F9731622", fg: "#F97316", label: "降級",   sev: 2 },
  unhealthy: { bg: "#EF444422", fg: "#EF4444", label: "異常",   sev: 3 },
  critical:  { bg: "#7F1D1D",   fg: "#FCA5A5", label: "嚴重",   sev: 4 },
  unknown:   { bg: "#6B728022", fg: "#9CA3AF", label: "未知",   sev: -1 },
};

const STATUS_TO_NUM = { healthy: 0, warning: 1, degraded: 2, unhealthy: 3, critical: 4 };

function statusMeta(s) { return STATUS[s] || STATUS.unknown; }

function truncateMiddle(s, head = 8, tail = 5) {
  if (!s || s.length <= head + tail + 1) return s || "";
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

function relativeTime(iso) {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (!isFinite(ts)) return iso;
  const diffSec = (Date.now() - ts) / 1000;
  if (diffSec < 5)    return "剛剛";
  if (diffSec < 60)   return `${Math.floor(diffSec)} 秒前`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分鐘前`;
  if (diffSec < 86400)return `${Math.floor(diffSec / 3600)} 小時前`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} 天前`;
  return new Date(iso).toISOString().slice(0, 16).replace("T", " ");
}

function isStale(iso, thresholdSec = 300) {
  if (!iso) return true;
  return (Date.now() - new Date(iso).getTime()) / 1000 > thresholdSec;
}

// ============================================================================
// SSE — fetch + ReadableStream (EventSource doesn't send Authorization)
// ============================================================================

function parseSSEBlock(block) {
  const lines = block.split("\n");
  let event = "message";
  let data = "";
  for (const line of lines) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
  }
  if (!data) return null;
  try { return { event, data: JSON.parse(data) }; }
  catch { return { event, data }; }
}

// ============================================================================
// Pill / dot
// ============================================================================

function StatusDot({ status, pulsing }) {
  const m = statusMeta(status);
  return (
    <span style={{
      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
      background: m.fg, boxShadow: pulsing ? `0 0 0 0 ${m.fg}` : "none",
      animation: pulsing ? "deviceops-pulse 1.4s infinite" : "none",
      flexShrink: 0,
    }} />
  );
}

function StatusPill({ status }) {
  const m = statusMeta(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.fg,
    }}>
      <StatusDot status={status} pulsing={status === "critical"} />
      {m.label}
    </span>
  );
}

// ============================================================================
// Sparkline
// ============================================================================

function Sparkline({ history, color, height = 32 }) {
  const data = useMemo(() => {
    if (!Array.isArray(history)) return [];
    return history.map((p, i) => ({
      i,
      v: STATUS_TO_NUM[p.status] ?? null,
      ts: p.observed_at,
    })).filter(p => p.v !== null);
  }, [history]);

  if (data.length === 0) {
    return <div style={{ height, color: t.dim, fontSize: 11, display: "flex", alignItems: "center" }}>暫無資料</div>;
  }

  return (
    <div style={{ height, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <YAxis domain={[0, 4]} hide />
          <XAxis dataKey="i" hide />
          <Tooltip
            contentStyle={{ background: t.card, border: `1px solid ${t.border}`, fontSize: 11 }}
            labelStyle={{ color: t.textDim }}
            formatter={(v) => {
              const key = Object.keys(STATUS_TO_NUM).find(k => STATUS_TO_NUM[k] === v);
              return [statusMeta(key).label, "status"];
            }}
            labelFormatter={(_, p) => p?.[0]?.payload?.ts ? relativeTime(p[0].payload.ts) : ""}
          />
          <Line type="stepAfter" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// Toast (lightweight inline)
// ============================================================================

function Toast({ msg, kind, onClose }) {
  useEffect(() => {
    if (!msg) return;
    const tm = setTimeout(onClose, 3500);
    return () => clearTimeout(tm);
  }, [msg, onClose]);
  if (!msg) return null;
  const colors = {
    success: { bg: t.successBg, fg: t.success, border: t.success },
    error:   { bg: t.dangerBg,  fg: t.danger,  border: t.danger },
    info:    { bg: t.accentBg,  fg: t.accentLight, border: t.accent },
  }[kind] || { bg: t.card, fg: t.text, border: t.border };
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 1000,
      padding: "12px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
      background: colors.bg, color: colors.fg, border: `1px solid ${colors.border}`,
      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    }}>{msg}</div>
  );
}

// ============================================================================
// Modal shell
// ============================================================================

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
        padding: 24, minWidth: 420, maxWidth: 540, color: t.text,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export default function DeviceOpsPage({ account }) {
  const [view, setView] = useState("list");
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [sseState, setSseState] = useState("connecting"); // connecting | live | reconnecting | offline
  const [lastBusEvent, setLastBusEvent] = useState(null);

  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // { kind, device }

  const sseAbortRef = useRef(null);
  const sseAttemptRef = useRef(0);

  // ── Initial list load ────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await api("/api/partner/deviceops/devices");
      // API contract: { success, data: { devices: [], summary: {} } }
      // Fall through to legacy/test shapes ({ devices } or array) for safety.
      const payload = (json && json.data) || json || {};
      const rawList = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload.devices) ? payload.devices : []);
      // Normalize: API field is `health_status`, page reads `.status`.
      // (Same convention as VenueCreativesPage's unwrapCreative.)
      const list = rawList.map(d => ({
        ...d,
        status: d.status ?? d.health_status ?? "unknown",
      }));
      const sum = payload.summary || json.summary || null;
      setDevices(list);
      setSummary(sum);
    } catch (e) {
      if (e.status === 401 || e.status === 403) setError("auth");
      else setError("load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Partner-wide SSE ──────────────────────────────────────────────────
  const applyEvent = useCallback((evt) => {
    setLastBusEvent({ type: evt.event, payload: evt.data, at: Date.now() });
    const d = evt.data || {};
    const deviceId = d.device_id || d.deviceId || d.id;
    if (!deviceId) return;

    if (evt.event === "health.snapshot" || evt.event === "health_snapshot") {
      setDevices(prev => prev.map(row => row.device_id === deviceId ? {
        ...row,
        // Bus payload uses health_status; normalize to .status for the page.
        status: d.status ?? d.health_status ?? row.status,
        health_status: d.health_status ?? d.status ?? row.health_status,
        last_sync_at: d.last_sync_at ?? row.last_sync_at,
        last_sync_status: d.last_sync_status ?? row.last_sync_status,
        last_upload_status: d.last_upload_status ?? row.last_upload_status,
        cache_total_count: d.cache_total_count ?? row.cache_total_count,
        proof_pending_count: d.proof_pending_count ?? row.proof_pending_count,
        player_version: d.player_version ?? row.player_version,
        updated_at: d.received_at ?? d.updated_at ?? row.updated_at,
      } : row));
    } else if (evt.event === "device.revoked" || evt.event === "device_revoked") {
      setDevices(prev => prev.map(row => row.device_id === deviceId
        ? { ...row, lifecycle_state: "REVOKED", status: "unknown" } : row));
    } else if (evt.event === "device.rebound" || evt.event === "device_rebound") {
      setDevices(prev => prev.map(row => row.device_id === deviceId
        ? { ...row, venue_id: d.venue_id ?? row.venue_id, zone_id: d.zone_id ?? row.zone_id } : row));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("biz_token");
    if (!token) { setSseState("offline"); return; }

    const connect = async () => {
      if (cancelled) return;
      const ctrl = new AbortController();
      sseAbortRef.current = ctrl;
      try {
        setSseState(sseAttemptRef.current === 0 ? "connecting" : "reconnecting");
        const res = await fetch(`${API}/api/partner/deviceops/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) throw new Error("sse_failed");
        sseAttemptRef.current = 0;
        setSseState("live");
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const parsed = parseSSEBlock(block);
            if (parsed) applyEvent(parsed);
          }
        }
      } catch (e) {
        if (cancelled || e.name === "AbortError") return;
      }
      if (cancelled) return;
      const attempt = ++sseAttemptRef.current;
      const backoffs = [1000, 2000, 5000, 10000, 30000];
      const delay = backoffs[Math.min(attempt - 1, backoffs.length - 1)];
      setSseState(attempt > backoffs.length ? "offline" : "reconnecting");
      setTimeout(() => { if (!cancelled) connect(); }, delay);
    };

    connect();
    return () => {
      cancelled = true;
      if (sseAbortRef.current) sseAbortRef.current.abort();
    };
  }, [applyEvent]);

  // ── Detail load ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedDeviceId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    api(`/api/partner/deviceops/devices/${selectedDeviceId}`)
      .then(json => {
        if (cancelled) return;
        // API contract: { success, data: { device, health_history, audit } }
        // Fall through to flat shape for safety.
        const payload = (json && json.data) || json || {};
        const rawDevice = payload.device || json.device || json || {};
        // Normalize: API field is `health_status`, page reads `.status`.
        const device = {
          ...rawDevice,
          status: rawDevice.status ?? rawDevice.health_status ?? "unknown",
        };
        // Normalize history entries the same way (sparkline reads p.status).
        const rawHistory = payload.health_history || json.health_history || [];
        const history = rawHistory.map(p => ({
          ...p,
          status: p.status ?? p.health_status ?? "unknown",
        }));
        setDetail({
          device,
          health_history: history,
          audit: payload.audit || json.audit || [],
        });
      })
      .catch(e => {
        if (cancelled) return;
        if (e.status === 404) {
          setToast({ kind: "error", msg: "找不到該設備" });
          setView("list");
          setSelectedDeviceId(null);
        } else {
          setToast({ kind: "error", msg: "無法載入設備詳情" });
        }
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDeviceId]);

  // ── Actions ───────────────────────────────────────────────────────────
  const doRevoke = async (deviceId) => {
    try {
      await api(`/api/partner/deviceops/devices/${deviceId}/revoke`, {
        method: "POST",
        body: { reason: "portal_revoke_by_partner" },
      });
      setDevices(prev => prev.map(r => r.device_id === deviceId
        ? { ...r, lifecycle_state: "REVOKED", status: "unknown" } : r));
      if (detail?.device?.device_id === deviceId) {
        setDetail(d => d ? { ...d, device: { ...d.device, lifecycle_state: "REVOKED" } } : d);
      }
      setToast({ kind: "success", msg: "設備已撤銷" });
      setModal(null);
    } catch (e) {
      if (e.status === 409 || e.code === "ALREADY_REVOKED") {
        setToast({ kind: "info", msg: "已撤銷" });
        setModal(null);
      } else if (e.status === 403) setToast({ kind: "error", msg: "權限不足" });
      else if (e.status === 404) setToast({ kind: "error", msg: "找不到該設備" });
      else setToast({ kind: "error", msg: "撤銷失敗，稍後重試" });
    }
  };

  const doRebind = async (deviceId, venueId, zoneId) => {
    try {
      await api(`/api/partner/deviceops/devices/${deviceId}/rebind`, {
        method: "POST",
        body: { venue_id: venueId, zone_id: zoneId || null },
      });
      setDevices(prev => prev.map(r => r.device_id === deviceId
        ? { ...r, venue_id: venueId, zone_id: zoneId || null } : r));
      if (detail?.device?.device_id === deviceId) {
        setDetail(d => d ? { ...d, device: { ...d.device, venue_id: venueId, zone_id: zoneId || null } } : d);
      }
      setToast({ kind: "success", msg: "綁定已更新" });
      setModal(null);
      return { ok: true };
    } catch (e) {
      if (e.status === 422 || e.code === "VENUE_ID_REQUIRED") {
        return { ok: false, fieldError: "venue_id", msg: "venue_id 為必填" };
      }
      if (e.status === 409 || e.code === "DEVICE_REVOKED") {
        setToast({ kind: "error", msg: "已撤銷的設備無法重新綁定" });
        setModal(null);
        return { ok: false };
      }
      return { ok: false, msg: e.message || "綁定失敗" };
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (error === "auth") {
    return (
      <PageShell>
        <div style={{ color: t.danger, fontSize: 14, padding: 24 }}>
          請重新登入後再試。<button onClick={() => { localStorage.removeItem("biz_token"); window.location.reload(); }}
            style={btnLink}>登出</button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <style>{`@keyframes deviceops-pulse {
        0% { box-shadow: 0 0 0 0 rgba(252,165,165,0.6); }
        70% { box-shadow: 0 0 0 8px rgba(252,165,165,0); }
        100% { box-shadow: 0 0 0 0 rgba(252,165,165,0); }
      }`}</style>

      <Header
        view={view}
        sseState={sseState}
        lastBusEvent={lastBusEvent}
        onBack={() => { setView("list"); setSelectedDeviceId(null); }}
        detail={detail}
      />

      {view === "list" && (
        <ListView
          loading={loading}
          error={error}
          devices={devices}
          summary={summary}
          onSelect={id => { setSelectedDeviceId(id); setView("detail"); }}
          onReload={loadList}
        />
      )}

      {view === "detail" && (
        <DetailView
          loading={detailLoading}
          detail={detail}
          onRevoke={d => setModal({ kind: "revoke", device: d })}
          onRebind={d => setModal({ kind: "rebind", device: d })}
        />
      )}

      <RevokeModal
        open={modal?.kind === "revoke"}
        device={modal?.device}
        onClose={() => setModal(null)}
        onConfirm={doRevoke}
      />
      <RebindModal
        open={modal?.kind === "rebind"}
        device={modal?.device}
        onClose={() => setModal(null)}
        onConfirm={doRebind}
      />

      <Toast msg={toast?.msg} kind={toast?.kind} onClose={() => setToast(null)} />
    </PageShell>
  );
}

// ============================================================================
// Page shell + header
// ============================================================================

function PageShell({ children }) {
  return (
    <div style={{ color: t.text, fontFamily: "DM Sans, Noto Sans TC, sans-serif" }}>
      {children}
    </div>
  );
}

function LiveIndicator({ state, lastBusEvent }) {
  const fresh = lastBusEvent && Date.now() - lastBusEvent.at < 30000;
  const map = {
    live:         { color: t.success, label: fresh ? "Live" : "Live (idle)", dot: "🟢" },
    connecting:   { color: t.warning, label: "Connecting…",                  dot: "🟡" },
    reconnecting: { color: t.warning, label: "Reconnecting…",                dot: "🟡" },
    offline:      { color: t.dim,     label: "Offline",                       dot: "⚪" },
  };
  const m = map[state] || map.connecting;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600,
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}33`,
    }}>
      <span>{m.dot}</span>{m.label}
    </span>
  );
}

function Header({ view, sseState, lastBusEvent, onBack, detail }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {view === "detail" && (
          <button onClick={onBack} style={btnLink}>← 返回</button>
        )}
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {view === "detail" && detail?.device
            ? `${truncateMiddle(detail.device.device_id)} · ${detail.device.device_name || "—"}`
            : "DeviceOps 設備運維"}
        </h2>
        {view === "detail" && detail?.device && <StatusPill status={detail.device.status} />}
      </div>
      <LiveIndicator state={sseState} lastBusEvent={lastBusEvent} />
    </div>
  );
}

// ============================================================================
// List view
// ============================================================================

function ListView({ loading, error, devices, summary, onSelect, onReload }) {
  const grouped = useGrouped(devices);
  const computedSummary = useMemo(() => summary || computeSummary(devices), [summary, devices]);

  if (loading) {
    return <div style={{ padding: 40, color: t.textDim, textAlign: "center" }}>載入中…</div>;
  }
  if (error === "load") {
    return (
      <div style={{ padding: 40, color: t.danger, textAlign: "center" }}>
        暫時無法取得設備資料，稍後請重試。
        <div><button onClick={onReload} style={btnLink}>重試</button></div>
      </div>
    );
  }

  return (
    <div>
      <SummaryCards summary={computedSummary} />
      {grouped.length === 0 && (
        <div style={{ padding: 40, color: t.textDim, textAlign: "center" }}>尚無設備</div>
      )}
      {grouped.map(venue => (
        <VenueBlock key={venue.venue_id || "__unbound__"} venue={venue} onSelect={onSelect} />
      ))}
    </div>
  );
}

function computeSummary(devices) {
  const by_status = {};
  let stale = 0, revoked = 0;
  for (const d of (Array.isArray(devices) ? devices : [])) {
    by_status[d.status] = (by_status[d.status] || 0) + 1;
    if (isStale(d.last_sync_at)) stale++;
    if (d.lifecycle_state === "REVOKED") revoked++;
  }
  return { total: devices.length, by_status, stale_count: stale, revoked_count: revoked };
}

function SummaryCards({ summary }) {
  if (!summary) return null;
  const cards = [
    { label: "全部",     val: summary.total,                         color: t.text },
    { label: "正常",     val: summary.by_status?.healthy   || 0,     color: STATUS.healthy.fg },
    { label: "警告",     val: summary.by_status?.warning   || 0,     color: STATUS.warning.fg },
    { label: "降級",     val: summary.by_status?.degraded  || 0,     color: STATUS.degraded.fg },
    { label: "異常",     val: summary.by_status?.unhealthy || 0,     color: STATUS.unhealthy.fg },
    { label: "嚴重",     val: summary.by_status?.critical  || 0,     color: STATUS.critical.fg },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 24 }}>
      {cards.map(c => (
        <div key={c.label} style={{
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: "14px 16px",
        }}>
          <div style={{ fontSize: 11, color: t.textDim, marginBottom: 4 }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.val}</div>
        </div>
      ))}
    </div>
  );
}

function useGrouped(devices) {
  return useMemo(() => {
    const byVenue = new Map();
    for (const d of (Array.isArray(devices) ? devices : [])) {
      const vk = d.venue_id || null;
      if (!byVenue.has(vk)) byVenue.set(vk, { venue_id: vk, venue_name: d.venue_name || null, zones: new Map() });
      const venue = byVenue.get(vk);
      const zk = d.zone_id || null;
      if (!venue.zones.has(zk)) venue.zones.set(zk, { zone_id: zk, devices: [] });
      venue.zones.get(zk).devices.push(d);
    }
    const venues = Array.from(byVenue.values()).map(v => ({
      ...v,
      zones: Array.from(v.zones.values()).map(z => ({
        ...z,
        devices: z.devices.slice().sort((a, b) => {
          const sa = statusMeta(a.status).sev, sb = statusMeta(b.status).sev;
          if (sa !== sb) return sb - sa;
          return (a.device_id || "").localeCompare(b.device_id || "");
        }),
      })).sort((a, b) => {
        if (a.zone_id === null) return 1;
        if (b.zone_id === null) return -1;
        return a.zone_id.localeCompare(b.zone_id);
      }),
    }));
    venues.sort((a, b) => {
      if (a.venue_id === null) return 1;
      if (b.venue_id === null) return -1;
      return a.venue_id.localeCompare(b.venue_id);
    });
    return venues;
  }, [devices]);
}

function VenueBlock({ venue, onSelect }) {
  const unbound = venue.venue_id === null;
  const title = unbound
    ? "未綁定場域"
    : `${venue.venue_name || ""} ${venue.venue_name ? "·" : ""} ${venue.venue_id}`.trim();
  return (
    <div style={{
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
      marginBottom: 16, overflow: "hidden",
    }}>
      <div style={{
        padding: "12px 16px", borderBottom: `1px solid ${t.border}`,
        fontSize: 13, fontWeight: 600, color: unbound ? t.warning : t.accentLight,
        background: unbound ? t.warningBg : "transparent",
      }}>
        🏢 {title}
      </div>
      {venue.zones.map(zone => (
        <div key={zone.zone_id || "__nozone__"}>
          <div style={{
            padding: "8px 16px", fontSize: 11, color: t.textDim, background: "#00000022",
            fontFamily: t.mono, letterSpacing: 0.4,
          }}>
            {zone.zone_id || "(未指定區域)"} · {zone.devices.length} 台
          </div>
          {zone.devices.map(d => <DeviceRow key={d.device_id} device={d} onSelect={onSelect} />)}
        </div>
      ))}
    </div>
  );
}

function DeviceRow({ device, onSelect }) {
  const m = statusMeta(device.status);
  const stale = isStale(device.last_sync_at);
  const revoked = device.lifecycle_state === "REVOKED";
  return (
    <div
      onClick={() => onSelect(device.device_id)}
      style={{
        display: "grid",
        gridTemplateColumns: "auto 200px 1fr 160px 140px",
        gap: 16, alignItems: "center",
        padding: "12px 16px", borderTop: `1px solid ${t.border}`,
        cursor: "pointer", opacity: revoked ? 0.55 : 1,
        transition: "background 0.12s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = `${t.accent}08`}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <StatusPill status={device.status} />
      <div style={{ fontFamily: t.mono, fontSize: 12, color: t.text }}>
        {truncateMiddle(device.device_id)}
        {device.has_token && (
          <span
            title={`token v${device.token_version || "?"} · last seen ${relativeTime(device.token_last_seen_at) || "—"}`}
            style={{ marginLeft: 6, color: t.success, fontSize: 10 }}
          >🔐</span>
        )}
        {!device.has_token && !revoked && (
          <span
            title="no device token — would fail in enforce mode"
            style={{ marginLeft: 6, color: t.warning, fontSize: 10 }}
          >🔓</span>
        )}
        {revoked && <span style={{ marginLeft: 6, color: t.danger, fontSize: 10 }}>(已撤銷)</span>}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          title={device.device_name || ""}
          style={{
            fontSize: 13, color: t.text,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >{device.device_name || "—"}</div>
        <div style={{ fontSize: 11, color: t.dim }}>v{device.player_version || "—"}</div>
      </div>
      <Sparkline history={device.health_history || []} color={m.fg} />
      <div style={{ textAlign: "right", fontSize: 11, color: stale ? t.danger : t.textDim }}>
        {relativeTime(device.last_sync_at)}
        {stale && <span style={{ marginLeft: 4 }}>⚠</span>}
      </div>
    </div>
  );
}

// ============================================================================
// Detail view
// ============================================================================

function DetailView({ loading, detail, onRevoke, onRebind }) {
  if (loading) return <div style={{ padding: 40, color: t.textDim, textAlign: "center" }}>載入中…</div>;
  if (!detail) return null;

  const { device, health_history, audit } = detail;
  const m = statusMeta(device.status);
  const revoked = device.lifecycle_state === "REVOKED";

  return (
    <div>
      <div style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
        padding: 20, marginBottom: 16,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          <KV label="Tenant"   val={device.tenant_id || "—"} mono />
          <KV label="Venue"    val={device.venue_id  || <span style={{ color: t.warning }}>未綁定</span>} />
          <KV label="Zone"     val={device.zone_id   || "—"} />
          <KV label="Player"   val={device.player_version || "—"} mono />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 16 }}>
          <KV label="Last sync"     val={relativeTime(device.last_sync_at)} />
          <KV label="Lifecycle"     val={device.lifecycle_state || "—"} />
          <KV label="Cache"         val={`total=${device.cache_total_count ?? "—"}`} mono />
          <KV label="Proof"         val={`pending=${device.proof_pending_count ?? "—"}  dead=${device.proof_dead_letter_count ?? "—"}`} mono />
        </div>
        {/* D3 — Device Token status row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 16 }}>
          <KV
            label="Token"
            val={
              device.has_token
                ? <span style={{ color: t.success }}>🔐 已綁定</span>
                : <span style={{ color: t.warning }}>🔓 未綁定</span>
            }
          />
          <KV label="Token ver" val={device.token_version != null ? `v${device.token_version}` : "—"} mono />
          <KV label="Token 簽發" val={relativeTime(device.token_issued_at)} />
          <KV label="Token 上次使用" val={relativeTime(device.token_last_seen_at)} />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={() => onRevoke(device)} disabled={revoked} style={revoked ? btnDisabled : btnDanger}>
            撤銷設備
          </button>
          <button onClick={() => onRebind(device)} disabled={revoked} style={revoked ? btnDisabled : btnPrimary}>
            重新綁定
          </button>
        </div>
      </div>

      <div style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
        padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>24 小時健康歷史</div>
        <div style={{ height: 140 }}>
          <Sparkline history={health_history} color={m.fg} height={140} />
        </div>
      </div>

      <div style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>近期審計 ({audit.length})</div>
        {audit.length === 0 && <div style={{ color: t.dim, fontSize: 12 }}>暫無紀錄</div>}
        {audit.slice(0, 20).map((row, i) => (
          <AuditRow key={i} row={row} isLast={i >= Math.min(audit.length, 20) - 1} />
        ))}
      </div>
    </div>
  );
}

function AuditRow({ row, isLast }) {
  const [open, setOpen] = useState(false);
  const ts = row.created_at ? relativeTime(row.created_at) : "—";
  const action = row.action || row.event_type || row.kind || "unknown";
  // Pretty-print details with a compact summary if it's an object.
  let detailsObj = row.details;
  if (typeof detailsObj === "string") {
    try { detailsObj = JSON.parse(detailsObj); } catch (_) { /* keep as string */ }
  }
  const hasDetails = detailsObj && (typeof detailsObj === "object" ? Object.keys(detailsObj).length > 0 : String(detailsObj).length > 0);
  // Short summary: pick the most useful 1-2 fields
  let summary = "";
  if (detailsObj && typeof detailsObj === "object") {
    const keys = ["mode", "reason", "from", "to", "venue_id", "zone_id", "health_status", "player_version"];
    const parts = [];
    for (const k of keys) {
      if (detailsObj[k] !== undefined && detailsObj[k] !== null) {
        const v = typeof detailsObj[k] === "object" ? JSON.stringify(detailsObj[k]) : String(detailsObj[k]);
        parts.push(`${k}=${v.length > 30 ? v.slice(0, 27) + "…" : v}`);
        if (parts.length >= 2) break;
      }
    }
    summary = parts.join("  ");
  }
  return (
    <div style={{
      padding: "8px 0",
      borderBottom: isLast ? "none" : `1px solid ${t.border}`,
      fontSize: 12,
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "110px 1fr auto",
        gap: 12,
        alignItems: "baseline",
      }}>
        <div style={{ color: t.textDim, fontFamily: t.mono }}>{ts}</div>
        <div style={{ color: t.text, fontFamily: t.mono }}>
          <span style={{ fontWeight: 600 }}>{action}</span>
          {summary && <span style={{ marginLeft: 12, color: t.textDim, fontWeight: 400 }}>{summary}</span>}
        </div>
        {hasDetails && (
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              background: "transparent", border: "none", color: t.accent,
              fontSize: 11, cursor: "pointer", padding: "0 4px",
            }}
          >{open ? "▾" : "▸"}</button>
        )}
      </div>
      {open && hasDetails && (
        <pre style={{
          marginTop: 8, padding: 10, background: t.bg, border: `1px solid ${t.border}`,
          borderRadius: 6, fontSize: 11, fontFamily: t.mono, color: t.textDim,
          overflowX: "auto", whiteSpace: "pre", maxHeight: 240,
        }}>{typeof detailsObj === "object" ? JSON.stringify(detailsObj, null, 2) : String(detailsObj)}</pre>
      )}
    </div>
  );
}

function KV({ label, val, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: t.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: t.text, fontFamily: mono ? t.mono : undefined }}>{val}</div>
    </div>
  );
}

// ============================================================================
// Modals
// ============================================================================

function RevokeModal({ open, device, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setBusy(false); }, [open]);
  if (!open || !device) return null;
  return (
    <Modal open={open} onClose={onClose} title="撤銷設備">
      <div style={{ fontSize: 13, color: t.textDim, lineHeight: 1.6, marginBottom: 20 }}>
        確定要撤銷設備 <span style={{ fontFamily: t.mono, color: t.text }}>{device.device_id}</span>？
        此操作會立即清除設備 token，下次同步將被拒絕。
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={btnSecondary} disabled={busy}>取消</button>
        <button
          onClick={async () => { setBusy(true); await onConfirm(device.device_id); setBusy(false); }}
          disabled={busy}
          style={btnDanger}
        >{busy ? "處理中…" : "撤銷"}</button>
      </div>
    </Modal>
  );
}

function RebindModal({ open, device, onClose, onConfirm }) {
  const [venueId, setVenueId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (open && device) {
      setVenueId(device.venue_id || "");
      setZoneId(device.zone_id || "");
      setBusy(false);
      setFieldError(null);
      setMsg(null);
    }
  }, [open, device]);

  if (!open || !device) return null;

  const submit = async () => {
    setFieldError(null);
    setMsg(null);
    if (!venueId.trim()) { setFieldError("venue_id"); setMsg("venue_id 為必填"); return; }
    setBusy(true);
    const r = await onConfirm(device.device_id, venueId.trim(), zoneId.trim());
    setBusy(false);
    if (!r.ok) {
      if (r.fieldError) setFieldError(r.fieldError);
      if (r.msg) setMsg(r.msg);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="重新綁定">
      <div style={{ fontSize: 13, color: t.textDim, marginBottom: 16 }}>
        設備 <span style={{ fontFamily: t.mono, color: t.text }}>{device.device_id}</span>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={fieldLabel}>Venue ID *</label>
        <input
          value={venueId}
          onChange={e => setVenueId(e.target.value)}
          placeholder="ven_xxx"
          style={{ ...inputStyle, borderColor: fieldError === "venue_id" ? t.danger : t.border }}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabel}>Zone ID</label>
        <input value={zoneId} onChange={e => setZoneId(e.target.value)} placeholder="ZONE-001" style={inputStyle} />
      </div>
      {msg && <div style={{ color: t.danger, fontSize: 12, marginBottom: 12 }}>{msg}</div>}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={btnSecondary} disabled={busy}>取消</button>
        <button onClick={submit} style={btnPrimary} disabled={busy}>{busy ? "儲存中…" : "儲存"}</button>
      </div>
    </Modal>
  );
}

// ============================================================================
// Styles
// ============================================================================

const btnBase = {
  padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
  cursor: "pointer", border: "1px solid transparent", transition: "opacity 0.15s",
};
const btnPrimary   = { ...btnBase, background: t.accent,    color: "#fff",  borderColor: t.accent };
const btnSecondary = { ...btnBase, background: "transparent", color: t.text, borderColor: t.border };
const btnDanger    = { ...btnBase, background: t.danger,    color: "#fff",  borderColor: t.danger };
const btnDisabled  = { ...btnBase, background: t.card, color: t.dim, borderColor: t.border, cursor: "not-allowed", opacity: 0.5 };
const btnLink      = {
  background: "transparent", border: "none", color: t.accentLight,
  cursor: "pointer", fontSize: 13, padding: "4px 8px",
};
const inputStyle = {
  width: "100%", padding: "10px 12px", background: t.bg, border: `1px solid ${t.border}`,
  borderRadius: 8, color: t.text, fontSize: 13, fontFamily: t.mono,
  outline: "none", boxSizing: "border-box",
};
const fieldLabel = {
  display: "block", fontSize: 11, color: t.textDim, marginBottom: 6,
  textTransform: "uppercase", letterSpacing: 0.6,
};
